import {
  ForwardedRef,
  forwardRef,
  lazy,
  MutableRefObject,
  ReactElement,
  ReactNode,
  Suspense,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Editor } from '@tiptap/core';
import Document from '@tiptap/extension-document';
import History from '@tiptap/extension-history';
import Link from '@tiptap/extension-link';
import Paragraph from '@tiptap/extension-paragraph';
import Placeholder from '@tiptap/extension-placeholder';
import Text from '@tiptap/extension-text';
import Underline from '@tiptap/extension-underline';
import { EditorContent, Extensions, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import styled from 'styled-components';

import { Button } from '@components/button';
import { FormError } from '@components/form-error';
import { FieldDescription, FormLabel } from '@components/form-label';
import { IconProps } from '@components/icons';
import { IconTextFormatBold } from '@components/icons/text-format-bold';
import { IconTextFormatBullet } from '@components/icons/text-format-bullet';
import { IconTextFormatItalic } from '@components/icons/text-format-italic';
import { IconTextFormatLink } from '@components/icons/text-format-link';
import { IconTextFormatList } from '@components/icons/text-format-list';
import { IconTextFormatSize } from '@components/icons/text-format-size';
import { IconTextFormatUnderline } from '@components/icons/text-format-underline';
import { Stack } from '@components/stack';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { transitionStandard } from '@utils/transitions';

import { NbspReplacer } from './addons/nbsp-replacer';
import OneLiner from './addons/one-liner';

const LinkButton = lazy(() => import('./addons/link-button'));

export type EditorValue<T = {}> = { value: string } & T;

export type BaseTextEditorProps<T = {}> = {
  onChange?(value: EditorValue<T>): void;
  onFocus?(e: React.FocusEvent<HTMLDivElement>): void;
  onEditorBlur?(editor: Editor): void;
  singleLine?: boolean;
  plaintext?: boolean;
  label?: string;
  hideLabel?: boolean;
  labelIcon?: ReactElement<IconProps>;
  labelRightContent?: ReactNode;
  placeholder?: string;
  error?: string;
  actions?: ReactNode;
  extensions?: Extensions;
  minHeight?: string;
  disabled?: boolean;
  info?: string;
  className?: string;
  description?: string;
  dataTestid?: string;
};

export type TextEditorProps<T = {}> = BaseTextEditorProps<T> & {
  defaultValue?: EditorValue<T>;
  helpText?: string;
  isActive?: boolean;
};

export type EditorMethods = {
  insertContent(content: string): void;
  replaceContent(content: string): void;
};

const escapeHtml = (content: string = '', whitelist: Array<string>) => {
  if (!content) {
    return content;
  }
  return content.replace(/<\/?([a-zA-Z0-9-]+)(\s[^<>]*)?>/g, (match, tagName) => {
    if (whitelist.includes(tagName)) {
      return match;
    } else {
      return match.replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }
  });
};

export const TextEditor = forwardRef(
  (
    {
      actions,
      extensions = [],
      defaultValue,
      plaintext = false,
      singleLine = false,
      onEditorBlur,
      onChange,
      onFocus,
      error,
      label,
      labelIcon,
      hideLabel = false,
      disabled = false,
      // 4.5rem = 3 lines at the 24px content line-height
      minHeight = singleLine ? undefined : plaintext ? '6.5rem' : '4.5rem',
      placeholder,
      info,
      helpText = '',
      isActive = false,
      labelRightContent,
      description,
      dataTestid,
      ...rest
    }: TextEditorProps,
    ref: ForwardedRef<EditorMethods>,
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const editorRef: MutableRefObject<Editor | null> = useRef(null);
    const commonExtensions = [
      Placeholder.configure({
        placeholder,
        showOnlyWhenEditable: false,
      }),
      ...extensions,
    ];

    useImperativeHandle(ref, () => ({
      insertContent: (content) => {
        if (editorRef.current?.isEditable) {
          editorRef.current?.chain().focus().insertContent(content).run();
        } else {
          // If the editor is not editable, it is also not focusable, and causes UX glitches when trying to focus it
          editorRef.current?.chain().insertContent(content).run();
        }
      },
      replaceContent: (content) => {
        if (editorRef.current?.isEditable) {
          editorRef.current?.chain().focus().selectAll().insertContent(content).run();
        } else {
          editorRef.current?.chain().selectAll().insertContent(content).run();
        }
      },
    }));

    const editor = useEditor({
      editable: !disabled,
      editorProps: {
        attributes: dataTestid ? { 'data-testid': dataTestid } : {},
      },
      onBlur: ({ editor }) => onEditorBlur?.(editor),
      parseOptions: {
        preserveWhitespace: 'full',
      },
      ...(plaintext
        ? {
            content: escapeHtml(
              defaultValue?.value,
              commonExtensions.map((extension) => extension.config.tag),
            ),
            extensions: [
              singleLine
                ? OneLiner
                : Document.extend({
                    // Solution to allow new lines in plaintext, based on
                    // https://discuss.prosemirror.net/t/allow-n-in-text-nodes/6354
                    content: 'inline*',
                    whitespace: 'pre',
                    addKeyboardShortcuts() {
                      return {
                        Enter: () => this.editor.commands.insertContent('\n'),
                      };
                    },
                  }),
              Paragraph,
              Text,
              NbspReplacer,
              History,
              ...commonExtensions,
            ],
            onUpdate: ({ editor }) => onChange?.({ value: editor.getText() }),
          }
        : {
            content: defaultValue?.value,
            extensions: [
              StarterKit,
              Underline,
              Link.configure({
                openOnClick: false,
              }),
              ...commonExtensions,
            ],

            onUpdate: ({ editor }) => onChange?.({ value: editor.getHTML() }),
          }),
    });

    const toolbarButtons = useMemo(
      () =>
        plaintext || !editor
          ? []
          : [
              {
                id: 'heading',
                icon: <IconTextFormatSize />,
                onClick: () => editor?.chain().focus().toggleHeading({ level: 3 }).run(),
              },
              {
                id: 'bold',
                icon: <IconTextFormatBold />,
                onClick: () => editor?.chain().focus().toggleBold().run(),
              },
              {
                id: 'italic',
                icon: <IconTextFormatItalic />,
                onClick: () => editor?.chain().focus().toggleItalic().run(),
              },
              {
                id: 'underline',
                icon: <IconTextFormatUnderline />,
                onClick: () => editor?.chain().focus().toggleUnderline().run(),
              },
              {
                id: 'bulletList',
                icon: <IconTextFormatBullet />,
                onClick: () => editor?.chain().focus().toggleBulletList().run(),
              },
              {
                id: 'orderedList',
                icon: <IconTextFormatList />,
                onClick: () => editor?.chain().focus().toggleOrderedList().run(),
              },
            ],
      [editor, plaintext],
    );

    editorRef.current = editor;

    const formLabel = <FormLabel text={label} icon={labelIcon} info={info} helpText={helpText} />;
    const labelRow = labelRightContent ? (
      <Stack orientation="horizontal" align="center" justify="space-between">
        {formLabel}
        {labelRightContent}
      </Stack>
    ) : (
      formLabel
    );

    return (
      <Stack space={1} {...rest}>
        {label && !hideLabel && labelRow}
        <FieldDescription text={description} />
        <Container
          space={2}
          $isFocused={isFocused || isActive}
          $plaintext={plaintext}
          data-error={Boolean(error)}
        >
          {!plaintext && (
            <ToolbarContainer>
              <Stack orientation="horizontal" wrap>
                {toolbarButtons.map((button, index) => (
                  <ToolbarButton
                    type="button"
                    key={index}
                    onClick={button.onClick}
                    data-active={editor && editor.isActive(button.id)}
                    data-testid={`text-editor-${button.id}`}
                  >
                    {button.icon}
                  </ToolbarButton>
                ))}
                <Suspense
                  fallback={
                    <Button
                      variant="ghost"
                      size="sm"
                      isDisabled
                      iconLeft={<IconTextFormatLink />}
                    />
                  }
                >
                  {editor && <LinkButton editor={editor} />}
                </Suspense>
              </Stack>
              <Stack orientation="horizontal" wrap>
                {actions}
              </Stack>
            </ToolbarContainer>
          )}
          <Stack
            orientation="horizontal"
            justify="space-between"
            space={2}
            onFocus={disabled ? onFocus : undefined}
            tabIndex={disabled ? 0 : undefined}
          >
            <StyledEditor
              editor={editor}
              onFocus={(e) => {
                if (!disabled) {
                  onFocus?.(e);
                }
                setIsFocused(true);
              }}
              onBlur={() => setIsFocused(false)}
              $minHeight={minHeight}
            />
            {plaintext && actions && <ActionsRow>{actions}</ActionsRow>}
          </Stack>
        </Container>
        <FormError text={error} />
      </Stack>
    );
  },
);

export const padding = 8;

export const borderWidth = 1;

// The container owns all editor content inset (the ProseMirror/field-mapping layers add no horizontal
// padding). Text sits LEFT_INSET_PX from the left border in every mode, with a tighter right edge.
const LEFT_INSET_PX = 12;
const RIGHT_INSET_PX = 8;
// rich mode's vertical inset matches the left inset; plaintext has no toolbar row, so it sits
// tighter vertically — 6px centers a 26px pill row in the 40px control.
const RICH_VERTICAL_INSET_PX = 12;
const PLAINTEXT_VERTICAL_INSET_PX = 6;
// The single-row field height, and the one content row inside it. Trailing actions (the + button)
// center against one row — not the grown field — so they sit centered on the first row and stay
// top-anchored as the field grows into a multi-row list.
const FIELD_HEIGHT_PX = 40;
const ACTIONS_ROW_HEIGHT_PX = FIELD_HEIGHT_PX - 2 * (PLAINTEXT_VERTICAL_INSET_PX + borderWidth);

const TOOLBAR_BUTTON_SIZE_PX = 32;

const Container = styled(Stack)<{
  $isFocused: boolean;
  $plaintext: TextEditorProps['plaintext'];
}>(({ $isFocused, $plaintext }) => {
  const verticalPx = $plaintext ? PLAINTEXT_VERTICAL_INSET_PX : RICH_VERTICAL_INSET_PX;
  return {
    minHeight: pxToRem(FIELD_HEIGHT_PX),
    width: '100%',
    minWidth: 0,
    maxWidth: '100%',
    boxSizing: 'border-box',
    color: theme.color.textPrimary,
    transition: transitionStandard('all'),
    backgroundColor: $isFocused ? theme.form.bg.colorFocus : theme.form.bg.color,
    borderRadius: theme.form.border.radius,
    padding: `${pxToRem(verticalPx)} ${pxToRem(RIGHT_INSET_PX)} ${pxToRem(verticalPx)} ${pxToRem(
      LEFT_INSET_PX,
    )}`,
    border: `${theme.form.border.width} solid ${
      $isFocused ? theme.form.border.colorFocus : theme.form.border.color
    }`,
    ':hover': {
      backgroundColor: $isFocused ? theme.form.bg.colorFocus : theme.form.bg.colorHover,
    },
    '&[data-error="true"]': {
      borderColor: theme.color.error,
    },
    ...($plaintext && {
      p: {
        marginBlock: '0px',
      },
    }),
  };
});

// Holds the trailing actions (e.g. the + button) centered against one content row and pinned to the
// top, so they sit centered on the first row and stay top-right as a multi-value field grows.
const ActionsRow = styled.div({
  alignSelf: 'flex-start',
  display: 'flex',
  alignItems: 'center',
  minHeight: pxToRem(ACTIONS_ROW_HEIGHT_PX),
});

const ToolbarContainer = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  minWidth: 0,
  maxWidth: '100%',
});

const StyledEditor = styled(EditorContent)<{
  $minHeight?: string;
}>(({ $minHeight }) => ({
  flex: 1,
  minWidth: 0,
  overflowX: 'clip',
  '.ProseMirror': {
    minHeight: $minHeight ?? 'auto',
    lineHeight: pxToRem(24),
    outline: 'none',
    overflowWrap: 'anywhere',
    overflow: 'hidden',
    padding: pxToRem(0),
    'p.is-editor-empty:first-child': {
      position: 'relative',
    },
    // Absolutely position the placeholder so the caret stays at the start (it's out of
    // flow), anchored to the empty paragraph and vertically centered regardless of line-height.
    'p.is-editor-empty:first-child::before': {
      position: 'absolute',
      top: 0,
      left: 0,
      maxWidth: '100%',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      pointerEvents: 'none',
      color: theme.color.textHelper,
      font: theme.font.regular16,
      // inherit (after the font shorthand resets it) so the placeholder's line matches
      // the first text line — keeps it aligned with typed text regardless of line-height
      lineHeight: 'inherit',
      content: 'attr(data-placeholder)',
    },

    a: {
      color: `${theme.color.elementBlue100} !important`,
      textDecoration: 'underline !important',
    },
  },
  p: {
    margin: 0,
    padding: 0,

    '&:empty': {
      padding: '0.75rem 0',
    },
  },
  'li > p': {
    margin: 0,
  },
}));

export const ToolbarButton = styled.button({
  transition: transitionStandard('all'),
  borderRadius: '0.25rem',
  padding: 0,
  border: 'none',
  background: 'transparent',
  color: theme.color.elementPurple50,
  height: pxToRem(TOOLBAR_BUTTON_SIZE_PX),
  width: pxToRem(TOOLBAR_BUTTON_SIZE_PX),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  outline: 'none',

  '&:focus-visible': {
    boxShadow: theme.shadow.focus,
    backgroundColor: theme.color.uiTrans10,
    color: theme.color.purple80,
    '--shadow-focus-color': theme.color.purple50,
    '--shadow-focus-gap': 0,
    '--shadow-focus-width': '0.1rem',
  },

  '&:active': {
    transform: 'scale(0.97)',
  },

  '&[data-active="true"]': {
    color: theme.color.elementBlue100,
  },

  '&:hover': {
    cursor: 'pointer',
    backgroundColor: theme.color.uiTrans10,

    '&[data-active="false"]': {
      color: theme.color.elementPurple80,
    },
  },
});
